<template>
  <aside class="basic-aside">
    <a class="basic-aside__logo" :href="homeURL" />

    <div class="basic-aside__button-group">
      <div class="button-group__prompt" @click="onClickReferencePromptButton">
        参考指令
      </div>
      <div class="button-group__new-chat" @click="onClickNewChatButton">
        新建聊天
      </div>
    </div>

    <ol class="basic-aside__session-title-list">
      <li
        v-for="{ id, title } in sessionTitleList"
        :key="id"
        :class="{ active: sessionTitleListItemActiveClassName(id) }"
        class="list-item"
        @click="onClickTitleListItem(id)"
      >
        <div class="list-item__title-wrapper">
          <i class="list-item__icon el-icon-chat-line-round"></i>
          <p class="list-item__title">{{ title }}</p>
        </div>

        <div class="list-item__button-wrapper">
          <i
            class="list-item__icon el-icon-download"
            @click="onClickDownloadListItem(id)"
          ></i>
          <i
            class="list-item__icon el-icon-delete"
            @click="onClickDeleteListItem(id)"
          ></i>
        </div>
      </li>
    </ol>

    <ul class="basic-aside__function-list">
      <li
        v-if="!organized || signInMethod !== 'organizational'"
        class="list-item"
        @click="onClickRechargeListItem"
      >
        <i class="list-item__icon el-icon-wallet"></i>
        <p>充值</p>
      </li>
      <li class="list-item" @click="onClickServiceListItem">
        <i class="list-item__icon el-icon-service"></i>
        <p>联系客服</p>
      </li>
      <li v-if="signedIn" class="list-item" @click="onClickSignOutListItem">
        <i class="list-item__icon el-icon-back"></i>
        <p>退出</p>
      </li>
    </ul>

    <div
      :class="{ 'sign-in': signedIn }"
      class="basic-aside__user-info"
      @click="onClickUserInfo"
    >
      <div
        class="user-info__logo"
        :style="{ backgroundImage: profileBackgroundImageSource }"
      ></div>
      <div class="user-info__text-container">
        <p class="text-container__user-name">
          {{ formatUsername }}
        </p>
        <template v-if="signedIn">
          <template v-if="organized && signInMethod === 'organizational'">
            <p class="text-container__organization-name">
              {{ organizationName }}
            </p>
            <p class="text-container__balance-points">
              今日机构剩余
              <el-tooltip class="item" effect="light" placement="top">
                <template #content>
                  <p>ChatGPT-3.5和文心一言：提问1点/次</p>
                  <p>ChatGPT-4：提问30点/次</p>
                </template>
                <i class="el-icon-info"></i>
              </el-tooltip>
              ：{{ formatOrganizationalBalancePoints || 0 }}
              点
            </p>
          </template>
          <template v-else-if="!organized || signInMethod === 'individual'">
            <p class="text-container__balance-points">
              账户剩余
              <el-tooltip class="item" effect="light" placement="top">
                <template #content>
                  <p>ChatGPT-3.5和文心一言：提问1点/次</p>
                  <p>ChatGPT-4：提问30点/次</p>
                </template>
                <i class="el-icon-info"></i>
              </el-tooltip>
              ：{{ formatUserBalancePoints || 0 }} 点
            </p>
            <p class="text-container__valid-until">
              {{ formatExpiryTime }}
            </p>
          </template>
        </template>
      </div>
    </div>
    <div v-if="!signedIn" class="basic-aside__token-cost-tips">
      <p>ChatGPT-3.5和文心一言：提问1点/次</p>
      <p>ChatGPT-4：提问30点/次</p>
    </div>
    <div
      v-if="signInMethodToggleButtonVisible"
      class="basic-aside__toggle-sign-in-method-button"
    >
      <el-button type="primary" @click="onClickSignInMethodToggleButton">{{
        formatSignInMethodToggleButtonText
      }}</el-button>
    </div>
  </aside>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import imageProfilePlain from "@/assets/images/profile-plain.png";
import apiSession from "@/api/session.js";

export default {
  name: "BasicAside",
  data() {
    return {
      imageProfilePlain,
      sessionDetailList: [],
    };
  },
  computed: {
    ...mapState(["homeURL"]),
    ...mapState("user", [
      "signedIn",
      "organized",
      "signInMethod",
      "username",
      "userTotalPoints",
      "userUsedPoints",
      "expiryTime",
      "profileImageSource",
      "organizationName",
      "organizationalTotalPoints",
      "organizationalUsedPoints",
    ]),
    ...mapState("session", ["modelType", "modelTypeList", "sessionTitleList"]),

    profileBackgroundImageSource() {
      const { signedIn, profileImageSource } = this;
      return `url("${
        signedIn ? profileImageSource || imageProfilePlain : imageProfilePlain
      }")`;
    },
    formatUsername() {
      const { signedIn, username } = this;
      return signedIn ? username : "登录/注册";
    },
    formatOrganizationalBalancePoints() {
      const { organizationalTotalPoints, organizationalUsedPoints } = this;
      return organizationalTotalPoints - organizationalUsedPoints;
    },
    formatUserBalancePoints() {
      const { userTotalPoints, userUsedPoints } = this;
      return userTotalPoints - userUsedPoints;
    },
    formatExpiryTime() {
      const { expiryTime } = this;
      return dayjs(expiryTime).format("[有效期至] YYYY年MM月DD日");
    },
    signInMethodToggleButtonVisible() {
      const { signedIn, organized } = this;
      return signedIn && organized;
    },
    formatSignInMethodToggleButtonText() {
      const { signInMethod } = this;
      return signInMethod === "individual"
        ? "切换至机构权限登录"
        : "切换至个人权限登录";
    },
  },
  methods: {
    ...mapMutations(["setCustomerServiceDialogVisible"]),
    ...mapMutations("user", ["setSignedInMethod"]),
    ...mapActions(["openSignInDialog"]),
    ...mapActions("user", ["handleSignOut"]),

    // 删除会话接口
    async deleteSessionTitle(messageId) {
      const { modelType: model } = this;
      const params = { messageId, model };
      return await apiSession.deleteSessionTitle(params);
    },
    // 根据会话ID获取会话详情接口
    async fetchSessionDetailList(messageId) {
      const params = { messageId };
      return await apiSession.fetchSessionDetailList(params).catch((error) => {
        console.error("fetchSessionDetailList");
        throw error;
      });
    },

    // 删除会话接口操作
    async handleDeleteSessionTitle(id) {
      await this.deleteSessionTitle(id).catch((error) => {
        console.error("handleDeleteSessionTitle");
        throw error;
      });
    },
    // 根据会话ID获取会话详情接口操作
    async handleFetchSessionDetailList(messageId) {
      const { data } = await this.fetchSessionDetailList(messageId).catch(
        (error) => {
          console.error("handleFetchSessionDetailList");
          throw error;
        }
      );
      this.sessionDetailList = Array.isArray(data) ? data : [];
    },

    sessionTitleListItemActiveClassName(id) {
      const { id: currentRouteParamsId } = this.$route.params;
      return currentRouteParamsId === id;
    },
    async generateExcel() {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sheet 1");
      worksheet.addRow(["问答角色", "问答内容"]);
      const {
        sessionDetailList,
        signInMethod,
        username,
        organized,
        organizationName,
        modelType,
        modelTypeList,
      } = this;
      const [{ label: currentModelLabel }] = modelTypeList.filter(
        ({ value }) => value === modelType
      );
      for (const sessionDetailListItem of sessionDetailList) {
        const { role, content } = sessionDetailListItem || {};
        const formatRoleText =
          role === "user"
            ? organized
              ? signInMethod !== "organizational"
                ? username
                : organizationName
              : username
            : `社科问道${currentModelLabel}`;
        worksheet.addRow([formatRoleText, content]);
      }
      const bufferItem = await workbook.xlsx.writeBuffer().catch((error) => {
        console.error("generateExcel");
        throw error;
      });
      const buffer = [bufferItem];
      const type =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob(buffer, { type });
      const url = URL.createObjectURL(blob);
      const $link = document.createElement("a");
      $link.href = url;
      const excelFileName = dayjs(new Date()).format("YYYY-MM-DD_HH-mm-ss-SSS");
      $link.download = `问答记录_${excelFileName}.xlsx`;
      $link.style.display = "none";
      document.body.appendChild($link);
      $link.click();
      document.body.removeChild($link);
      URL.revokeObjectURL(url);
    },

    onClickReferencePromptButton() {
      const { signedIn } = this;
      if (!signedIn) {
        this.openSignInDialog();
        return;
      }
      const { name: currentRouteName } = this.$route;
      const name = "Prompt";
      if (currentRouteName === name) {
        return;
      }
      this.$router.push({ name });
    },
    onClickNewChatButton() {
      const { signedIn } = this;
      if (!signedIn) {
        this.openSignInDialog();
        return;
      }
      const { name: currentRouteName } = this.$route;
      const name = "Home";
      if (currentRouteName === name) {
        document.querySelector(".question__textarea").focus();
        return;
      }
      this.$router.push({ name });
    },
    onClickTitleListItem(id) {
      const { id: currentRouteParamsId } = this.$route.params;
      if (currentRouteParamsId === id) {
        return;
      }
      const name = "Session";
      const params = { id };
      this.$router.push({ name, params });
    },
    async onClickDownloadListItem(messageId) {
      await this.handleFetchSessionDetailList(messageId).catch((error) => {
        console.error("onClickDownloadListItem");
        throw error;
      });
      await this.generateExcel().catch((error) => {
        console.error("onClickDownloadListItem");
        throw error;
      });
    },
    async onClickDeleteListItem(id) {
      await this.handleDeleteSessionTitle(id).catch((error) => {
        console.error("onClickDeleteListItem");
        throw error;
      });
      const { name: currentRouteName } = this.$route;
      const name = "Home";
      if (currentRouteName === name) {
        location.reload();
        return;
      }
      this.$router.push({ name });
    },
    onClickRechargeListItem() {
      const { signedIn } = this;
      if (!signedIn) {
        this.openSignInDialog();
        return;
      }
      const { name: currentRouteName } = this.$route;
      const name = "Recharge";
      if (currentRouteName === name) {
        return;
      }
      this.$router.push({ name });
    },
    onClickServiceListItem() {
      this.setCustomerServiceDialogVisible(true);
    },
    async onClickSignOutListItem() {
      await this.handleSignOut().catch((error) => {
        console.error("onClickSignOutListItem");
        throw error;
      });
    },
    onClickUserInfo() {
      const { signedIn } = this;
      if (!signedIn) {
        this.openSignInDialog();
        return;
      }
    },
    onClickSignInMethodToggleButton() {
      const { signInMethod } = this;
      this.setSignedInMethod(
        signInMethod === "individual" ? "organizational" : "individual"
      );
      const { name: currentRouteName } = this.$route;
      const name = "Home";
      if (currentRouteName === name) {
        location.reload();
        return;
      }
      this.$router.push({ name });
    },
  },
};
</script>

<style lang="scss" scoped>
.basic-aside {
  display: none;
}

@media screen and (min-width: 768px) {
  .basic-aside {
    padding: 0 8px;
    display: block;
    width: 260px;
    min-height: 560px;
    color: var(--color-basic-white);
    font-size: 14px;
    background-color: var(--color-basic-black);

    .basic-aside__logo {
      margin: 8px 4px;
      display: block;
      height: 72px;
      background-image: url("~@/assets/images/logo.png");
      background-size: contain;
      background-repeat: no-repeat;
    }

    .basic-aside__button-group {
      display: flex;
      justify-content: space-between;

      .button-group__prompt,
      .button-group__new-chat {
        padding: 14px 0;
        width: 45%;
        text-align: center;
        border: 1px solid var(--color-level-1-border);
        border-radius: 4px;
        cursor: pointer;
      }
    }

    .list-item__icon {
      margin-right: 8px;
      font-size: 20px;
      width: 20px;
    }

    .basic-aside__session-title-list {
      margin-top: 16px;
      height: calc(var(--vh) * 100 - 424px);
      min-height: 48px;
      overflow: auto;
      border-bottom: 1px solid var(--color-level-1-border);

      .list-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-radius: 4px;
        cursor: default;

        .list-item__icon.el-icon-delete,
        .list-item__icon.el-icon-download {
          display: none;
          cursor: pointer;
        }

        &:hover {
          background-color: #343540;
        }

        &.active {
          background-color: #343540;

          .list-item__icon.el-icon-delete,
          .list-item__icon.el-icon-download {
            display: inline-block;
          }
        }

        .list-item__title-wrapper {
          display: flex;
          align-items: center;
          cursor: pointer;

          .list-item__title {
            display: -webkit-box;
            width: 120px;
            overflow: hidden;
            height: 40px;
            line-height: 40px;
            text-overflow: ellipsis;
            word-break: break-all;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
          }
        }

        .list-item__button-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
      }
    }

    .basic-aside__function-list {
      margin-top: 8px;
      padding: 8px 0;

      .list-item {
        display: flex;
        align-items: center;
        height: 40px;
        border-radius: 4px;
        cursor: pointer;

        &:hover {
          background-color: #343540;
        }
      }
    }

    .basic-aside__user-info {
      padding: 4px 0;
      display: flex;
      align-items: center;
      cursor: pointer;

      &.sign-in {
        cursor: default;
      }

      .user-info__logo {
        margin: 0 8px;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;
      }

      .user-info__text-container {
        line-height: 1.5;

        .text-container__user-name,
        .text-container__organization-name,
        .text-container__balance-points,
        .text-container__valid-until {
          display: -webkit-box;
          width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-all;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
        }
      }
    }

    .basic-aside__token-cost-tips {
      margin-top: 8px;
      line-height: 1.5;
    }

    .basic-aside__toggle-sign-in-method-button {
      margin-top: 8px;
      display: flex;
      justify-content: center;
    }
  }
}
</style>